/**
 * The Supabase client, and the token seam every authenticated request reads from.
 *
 * The backend issues no session of its own — Supabase Auth is its only authentication scheme, and
 * `/auth/me` and friends only ever verify a bearer that already exists (authentication ADR §Context).
 * So this file is where the session physically lives, and it is the only place in the app that
 * talks to supabase-js's auth surface.
 *
 * ## The four client options, and why each is what it is
 *
 * | Option | Value | Why |
 * |---|---|---|
 * | `persistSession` | `true` | A trainer leaves the plan builder open, reloads it, and works across tabs. |
 * | `autoRefreshToken` | `true` | No AppState to gate it on, unlike mobile — a browser tab is either open or gone. |
 * | `flowType` | `'pkce'` | The sign-up confirmation link returns a short-lived `?code`, not tokens in the URL fragment. |
 * | `detectSessionInUrl` | **on** (`isSupabaseUrlCallback`) | Mobile sets this `false` because it has no URL to parse — it signs in with native identity tokens. A browser DOES get the redirect back, and the PKCE code has to be exchanged from it. Copying mobile's value here would break email confirmation and every OAuth provider. The predicate only keeps Google's consent return out (see its file). |
 *
 * `storage` is our own adapter (`model/resilientStorage.ts`) rather than the library default,
 * for one reason: a failed read must never delete the session. See that file's header — it is a
 * production incident, not a precaution.
 *
 * **No custom `lock` is passed.** supabase-js coordinates refreshes itself and relies on the GoTrue
 * server to resolve cross-tab races; the installed typings say passing a lock "opts into a legacy
 * path … preserved for backwards compatibility (typically React Native `processLock`)". Multi-tab
 * behaviour therefore rides on the library's own contract, which authentication ADR §6 records as
 * needing a two-tab manual QA pass before ship.
 */

import { createClient, type SupabaseClient, type SupportedStorage } from '@supabase/supabase-js';

import { createResilientSessionStorage, type KeyValueStore } from '../model/resilientStorage';
import { isSupabaseUrlCallback } from '../model/supabaseUrlCallback';

/**
 * The browser's `localStorage`, or `null` when reaching for it throws. Merely *touching*
 * `window.localStorage` raises a `SecurityError` in a blocked third-party context, so even the
 * property access has to be guarded.
 */
function readBrowserLocalStorage(): KeyValueStore | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * The proof promised in `model/resilientStorage.ts`: our adapter's shape IS supabase-js's
 * `SupportedStorage`, so the model layer can describe it without importing from the library.
 */
function sessionStorageAdapter(): SupportedStorage {
  return createResilientSessionStorage(readBrowserLocalStorage()) satisfies SupportedStorage;
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

/** True only when both values are present — the single gate for attempting any Supabase call. */
export const isSupabaseConfigured: boolean = supabaseUrl !== '' && supabaseAnonKey !== '';

function createConfiguredClient(): SupabaseClient | null {
  if (!isSupabaseConfigured) {
    console.warn(
      '[auth] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not set. The app boots, but no ' +
        'session can be established: sign-in will report a connection error and every route ' +
        'resolves as signed out. Copy .env.example to .env.local, fill both in, and restart Vite.',
    );
    return null;
  }

  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      storage: sessionStorageAdapter(),
      persistSession: true,
      autoRefreshToken: true,
      // See the table above: the opposite of mobile, deliberately.
      detectSessionInUrl: isSupabaseUrlCallback,
      flowType: 'pkce',
    },
  });
}

const client: SupabaseClient | null = createConfiguredClient();

/** The client, or `null` when Supabase is unconfigured. Callers branch on null. */
export function getSupabaseClient(): SupabaseClient | null {
  return client;
}

/**
 * The single shared bearer source, and what `src/api/mutator.ts` attaches to every request.
 * `getSession()` returns the persisted session and transparently refreshes it when it has expired,
 * so a caller never sees a stale token.
 */
export async function getAccessToken(): Promise<string | null> {
  if (!client) return null;
  try {
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    // A storage failure means "no session" (see resilientStorage.ts) — never an error thrown into
    // the middle of a request.
    return null;
  }
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Single-flight session refresh — the seam `mutator.ts` calls on a 401.
 *
 * The mutator deliberately does NOT hold this guard itself: a transport-level guard would only
 * cover calls passing through that module, while a burst of 401s can arrive from anywhere. One
 * module-level promise shared by every caller collapses them into one `refreshSession()`.
 *
 * A `null` result is NOT terminal, and nothing here decides that it is. supabase-js classifies the
 * failure itself: a network-class failure preserves the session and emits no `SIGNED_OUT` (it
 * retries later on its own ticker), while a non-retryable failure against an already-expired token
 * makes it drop the session and emit `SIGNED_OUT`. The session layer reacts to that event; this
 * function only reports whether it got a token, and the caller surfaces its original 401 if not.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!client) return Promise.resolve(null);

  refreshInFlight ??= client.auth
    .refreshSession()
    .then(({ data, error }) => {
      if (error) {
        console.warn('[auth] session refresh failed', {
          name: error.name,
          status: error.status,
          code: error.code,
        });
        return null;
      }
      return data.session?.access_token ?? null;
    })
    .catch((error: unknown) => {
      console.warn('[auth] session refresh threw', error);
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}
